import { List } from 'immutable';
import PropTypes from 'prop-types';
import { Component } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import * as TwoFactorAuthSetupSteps from '../constants/TwoFactorAuthSetupSteps';
import * as TwoFactorAuthMethods from '../constants/TwoFactorAuthMethods';
import * as TwoFactorAuthPrecedenceTypes from '../constants/TwoFactorAuthPrecedenceTypes';
import TwoFactorAuthSMSErrors from '../constants/TwoFactorAuthSMSErrors';
import RemoveTwoFactorAuth from './RemoveTwoFactorAuth';
import withPartials from '../lib/withPartials';
import GoogleAuthQRCode from '../records/GoogleAuthQRCode';
import AsyncTwoFactorAuthSetup from './AsyncTwoFactorAuthSetup';
import AsyncTwoFactorAuthBackupSetup from './AsyncTwoFactorAuthBackupSetup';
import Subsection from 'settings-ui-lib/components/Subsection';
import SettingsRowButton from 'settings-ui-lib/components/SettingsRowButton';
import WithReAuth from 'two-factor-prompt-lib/containers/WithReAuth';
import TwoFactorSetupOrSkip from './TwoFactorSetupOrSkip';
import { trackTwoFactorSetup, TwoFactorAuthTypes, TwoFactorEventActions } from '../lib/tracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
class TwoFactorAuth extends Component {
  constructor(props) {
    super(props);
    this.state = {
      configStagedForRemoval: null
    };
    this.handleStageConfigForRemoval = this.handleStageConfigForRemoval.bind(this);
    this.handleCancelRemoval = this.handleCancelRemoval.bind(this);
    this.handleConfirmRemoval = this.handleConfirmRemoval.bind(this);
    this.handleOnClick = this.handleOnClick.bind(this);
  }
  handleStageConfigForRemoval(precedence) {
    this.setState({
      configStagedForRemoval: precedence
    });
  }
  handleCancelRemoval() {
    this.setState({
      configStagedForRemoval: null
    });
  }
  handleConfirmRemoval(precedence) {
    const {
      secondaryAuthMethod,
      primaryAuthMethod
    } = this.props;
    this.setState({
      configStagedForRemoval: null
    });
    switch (primaryAuthMethod) {
      case TwoFactorAuthMethods.HUBSPOT_AUTHENTICATOR:
        trackTwoFactorSetup(TwoFactorAuthTypes.HUBSPOT_AUTHENTICATOR, TwoFactorEventActions.REMOVED);
        break;
      case TwoFactorAuthMethods.GOOGLE_AUTHENTICATOR:
        trackTwoFactorSetup(TwoFactorAuthTypes.THIRD_PARTY_AUTHENTICATOR, TwoFactorEventActions.REMOVED);
        break;
      case TwoFactorAuthMethods.SMS:
        trackTwoFactorSetup(TwoFactorAuthTypes.SMS_CODES, TwoFactorEventActions.REMOVED);
        break;
      default:
    }
    this.props.onRemoveTwoFactorAuth(precedence, secondaryAuthMethod !== null);
  }
  handleOnClick(authMethod, precedence) {
    const {
      onClickSetup,
      partial
    } = this.props;
    const stagedMethod = TwoFactorAuthMethods.SMS;
    return authMethod === null ? partial(onClickSetup, precedence, stagedMethod) : partial(this.handleStageConfigForRemoval, precedence);
  }
  shouldShowSetup() {
    return this.props.setupStep !== TwoFactorAuthSetupSteps.UNINITIALIZED;
  }
  render() {
    const {
      backupModalVisible,
      canModify,
      email,
      invalidVerificationCode,
      onCancelSetup,
      onChangeSMSNumber,
      onChangeVerificationCodeAttempt,
      onCompleteSetup,
      onConfirmSetupStep,
      onKeyUp,
      onSelectMethod,
      partial,
      primaryAuthMethod,
      qrCode,
      redirectUrl,
      registerSmsError,
      resendSMSCode,
      secondaryAuthMethod,
      setupForLogin,
      setupForLoginAccountInfo,
      setupStep,
      shouldBlockSetupProgress,
      shouldRenderGoogleAuthWarning,
      show2FANudge,
      smsNumber,
      stagedAuthMethod,
      verificationCodeAttempt,
      isNewUser,
      unusedBackupCodes
    } = this.props;
    const shouldRenderBackup = unusedBackupCodes > 0 && primaryAuthMethod !== null;
    let renderedSetupAuth;
    if (!primaryAuthMethod) {
      renderedSetupAuth = /*#__PURE__*/_jsx(SettingsRowButton, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "userPreferences.2fa.setupLabel",
          "data-test-id": "2fa-setup-link"
        }),
        disabled: !canModify,
        onClick: this.handleOnClick(null, TwoFactorAuthPrecedenceTypes.PRIMARY),
        description: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "userPreferences.2fa.description"
        })
      });
    }
    let renderedPrimaryAuthMethod;
    if (primaryAuthMethod) {
      renderedPrimaryAuthMethod = /*#__PURE__*/_jsx(SettingsRowButton, {
        title: /*#__PURE__*/_jsx(WithReAuth, {
          onSuccess: this.handleOnClick(primaryAuthMethod, TwoFactorAuthPrecedenceTypes.PRIMARY),
          isDisabled: !canModify,
          email: email,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `userPreferences.2fa.removeLabel.${primaryAuthMethod}`,
            "data-test-id": "2fa-primary-remove"
          })
        }),
        disabled: !canModify,
        description: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "userPreferences.2fa.primaryDescription"
        })
      });
    }
    let renderedSecondaryAuthMethod;
    if (secondaryAuthMethod) {
      renderedSecondaryAuthMethod = /*#__PURE__*/_jsx(SettingsRowButton, {
        title: /*#__PURE__*/_jsx(WithReAuth, {
          onSuccess: this.handleOnClick(secondaryAuthMethod, TwoFactorAuthPrecedenceTypes.SECONDARY),
          isDisabled: !canModify,
          email: email,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `userPreferences.2fa.removeLabel.${secondaryAuthMethod}`
          })
        }),
        disabled: !canModify,
        description: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "userPreferences.2fa.secondaryDescription"
        })
      });
    } else {
      renderedSecondaryAuthMethod = /*#__PURE__*/_jsx(SettingsRowButton, {
        title: /*#__PURE__*/_jsx(WithReAuth, {
          onSuccess: this.handleOnClick(null, TwoFactorAuthPrecedenceTypes.SECONDARY),
          isDisabled: !canModify,
          email: email,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `userPreferences.2fa.setupSecondaryMethod`
          })
        }),
        disabled: !canModify,
        description: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "userPreferences.2fa.secondaryDescription"
        })
      });
    }
    let renderedBackup;
    if (primaryAuthMethod) {
      renderedBackup = /*#__PURE__*/_jsx(SettingsRowButton, {
        title: /*#__PURE__*/_jsx(WithReAuth, {
          onSuccess: this.props.onClickBackup,
          isDisabled: !canModify,
          email: email,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: shouldRenderBackup ? 'userPreferences.2fa.viewBackup' : 'userPreferences.2fa.setupBackup'
          })
        }),
        disabled: !canModify,
        description: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: unusedBackupCodes === 1 ? 'userPreferences.2fa.remainingCode' : 'userPreferences.2fa.remainingCodes',
          options: {
            unusedBackupCodes
          }
        })
      });
    }
    return /*#__PURE__*/_jsxs(Subsection, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "userPreferences.2fa.title"
      }),
      children: [!primaryAuthMethod && renderedSetupAuth, primaryAuthMethod && renderedPrimaryAuthMethod, primaryAuthMethod && renderedSecondaryAuthMethod, primaryAuthMethod && renderedBackup, show2FANudge && !secondaryAuthMethod && /*#__PURE__*/_jsx(TwoFactorSetupOrSkip, {
        primaryAuthMethod: this.props.primaryAuthMethod
      }), this.shouldShowSetup() && /*#__PURE__*/_jsx(AsyncTwoFactorAuthSetup, {
        backupCodes: this.props.backupCodes,
        backupCodesCreatedAt: this.props.backupCodesCreatedAt,
        backupCodesLoading: this.props.backupCodesLoading,
        canModify: canModify,
        confirmMethod: this.props.confirmMethod,
        confirmSMSNumber: this.props.confirmSMSNumber,
        invalidVerificationCode: invalidVerificationCode,
        method: stagedAuthMethod,
        onCancel: onCancelSetup,
        onChangeSMSNumber: onChangeSMSNumber,
        onChangeVerificationCodeAttempt: onChangeVerificationCodeAttempt,
        onComplete: onCompleteSetup,
        onConfirmStep: partial(onConfirmSetupStep, setupStep),
        onKeyUp: onKeyUp,
        onSelectMethod: onSelectMethod,
        precedence: this.props.precedence,
        primaryAuthInfo: this.props.primaryAuthInfo,
        primaryAuthMethod: primaryAuthMethod,
        printedOrSavedBackupCodes: this.props.printedOrSavedBackupCodes,
        qrCode: qrCode,
        redirectToSecondary2faSetup: this.props.redirectToSecondary2faSetup,
        redirectUrl: redirectUrl,
        registerSmsError: registerSmsError,
        unusedBackupCodes: unusedBackupCodes,
        requestTwoFactorAuthGenerateBackupCodes: this.props.requestTwoFactorAuthGenerateBackupCodes,
        resendSMSCode: resendSMSCode,
        setupForLogin: setupForLogin,
        setupForLoginAccountInfo: setupForLoginAccountInfo,
        shouldBlockSetupProgress: shouldBlockSetupProgress,
        shouldBlockVerifyButton: this.props.shouldBlockVerifyButton,
        shouldNotSetupOnLogin: this.props.shouldNotSetupOnLogin,
        shouldRenderGoogleAuthWarning: shouldRenderGoogleAuthWarning,
        shouldSetupSecondaryAuthMethod: this.props.shouldSetupSecondaryAuthMethod,
        smsNumber: smsNumber,
        step: setupStep,
        verificationCodeAttempt: verificationCodeAttempt,
        isNewUser: isNewUser,
        userId: this.props.userId
      }), this.state.configStagedForRemoval !== null ? /*#__PURE__*/_jsx(RemoveTwoFactorAuth, {
        precedence: this.state.configStagedForRemoval,
        method: this.state.configStagedForRemoval === TwoFactorAuthPrecedenceTypes.PRIMARY ? primaryAuthMethod : secondaryAuthMethod,
        secondaryIsEnabled: secondaryAuthMethod !== null,
        onConfirmRemoval: partial(this.handleConfirmRemoval, this.state.configStagedForRemoval, secondaryAuthMethod !== null),
        onConfirmRemoveAll: partial(this.handleConfirmRemoval, TwoFactorAuthPrecedenceTypes.ALL, secondaryAuthMethod !== null),
        onCancel: this.handleCancelRemoval
      }) : null, backupModalVisible && /*#__PURE__*/_jsx(AsyncTwoFactorAuthBackupSetup, {
        backupCodesCreatedAt: this.props.backupCodesCreatedAt,
        backupCodes: this.props.backupCodes,
        unusedBackupCodes: unusedBackupCodes,
        backupCodesLoading: this.props.backupCodesLoading,
        requestTwoFactorAuthGenerateBackupCodes: this.props.requestTwoFactorAuthGenerateBackupCodes,
        printedOrSavedBackupCodes: this.props.printedOrSavedBackupCodes,
        onCloseBackup: this.props.onCloseBackup,
        userId: this.props.userId
      })]
    });
  }
}
TwoFactorAuth.propTypes = {
  // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'typeof List' is not assignable t... Remove this comment to see the full error message
  backupCodes: PropTypes.instanceOf(List),
  backupCodesCreatedAt: PropTypes.number,
  backupCodesLoading: PropTypes.bool.isRequired,
  backupModalVisible: PropTypes.bool.isRequired,
  canModify: PropTypes.bool.isRequired,
  confirmMethod: PropTypes.func.isRequired,
  confirmSMSNumber: PropTypes.func.isRequired,
  email: PropTypes.string.isRequired,
  invalidVerificationCode: PropTypes.bool.isRequired,
  onCancelSetup: PropTypes.func.isRequired,
  onChangeSMSNumber: PropTypes.func.isRequired,
  onChangeVerificationCodeAttempt: PropTypes.func.isRequired,
  onClickBackup: PropTypes.func.isRequired,
  onClickSetup: PropTypes.func.isRequired,
  onCloseBackup: PropTypes.func.isRequired,
  onCompleteSetup: PropTypes.func.isRequired,
  onConfirmSetupStep: PropTypes.func.isRequired,
  onKeyUp: PropTypes.func.isRequired,
  onRemoveTwoFactorAuth: PropTypes.func.isRequired,
  onSelectMethod: PropTypes.func.isRequired,
  partial: PropTypes.func.isRequired,
  precedence: PropTypes.string.isRequired,
  primaryAuthInfo: PropTypes.string,
  primaryAuthMethod: PropTypes.oneOf(Object.keys(TwoFactorAuthMethods)),
  printedOrSavedBackupCodes: PropTypes.func.isRequired,
  qrCode: PropTypes.instanceOf(GoogleAuthQRCode),
  redirectToSecondary2faSetup: PropTypes.func.isRequired,
  redirectUrl: PropTypes.string,
  registerSmsError: PropTypes.oneOf(Object.keys(TwoFactorAuthSMSErrors)),
  requestTwoFactorAuthGenerateBackupCodes: PropTypes.func.isRequired,
  resendSMSCode: PropTypes.func.isRequired,
  secondaryAuthMethod: PropTypes.oneOf(Object.keys(TwoFactorAuthMethods)),
  setupForLogin: PropTypes.bool.isRequired,
  setupForLoginAccountInfo: PropTypes.object,
  setupStep: PropTypes.oneOf(Object.keys(TwoFactorAuthSetupSteps)).isRequired,
  shouldBlockSetupProgress: PropTypes.bool.isRequired,
  shouldBlockVerifyButton: PropTypes.bool.isRequired,
  shouldNotSetupOnLogin: PropTypes.bool.isRequired,
  shouldRenderGoogleAuthWarning: PropTypes.bool.isRequired,
  shouldSetupSecondaryAuthMethod: PropTypes.bool.isRequired,
  show2FANudge: PropTypes.bool.isRequired,
  smsNumber: PropTypes.string.isRequired,
  stagedAuthMethod: PropTypes.oneOf(Object.keys(TwoFactorAuthMethods)).isRequired,
  verificationCodeAttempt: PropTypes.string.isRequired,
  isNewUser: PropTypes.bool,
  unusedBackupCodes: PropTypes.number.isRequired,
  userId: PropTypes.number.isRequired
};
export default withPartials(TwoFactorAuth);